// usePresence.js - Track user presence and handle real-time chat notifications
import { useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';

export function usePresence(user, companyId, currentPage = 'unknown') {
  const presenceInterval = useRef(null);
  const lastUpdate = useRef(null);

  const updatePresence = useCallback(async (status = 'online') => {
    if (!user?.id) return;

    // Throttle updates to every 30 seconds
    const now = Date.now();
    if (lastUpdate.current && now - lastUpdate.current < 30000 && status === 'online') {
      return;
    }
    lastUpdate.current = now;

    try {
      await supabaseData
        .from('user_presence')
        .upsert({
          user_id: user.id,
          company_id: companyId,
          status,
          current_page: currentPage,
          last_seen: new Date().toISOString(),
          user_agent: navigator.userAgent
        }, {
          onConflict: 'user_id'
        });
    } catch (err) {
    }
  }, [user?.id, companyId, currentPage]);

  useEffect(() => {
    if (!user?.id) return;

    // Initial presence update
    updatePresence('online');

    // Update presence every 60 seconds
    presenceInterval.current = setInterval(() => {
      updatePresence('online');
    }, 60000);

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        updatePresence('away');
      } else {
        updatePresence('online');
      }
    };

    // Handle before unload
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable offline status
      const data = JSON.stringify({
        user_id: user.id,
        status: 'offline',
        last_seen: new Date().toISOString()
      });
      navigator.sendBeacon?.('/api/presence-offline', data);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(presenceInterval.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      updatePresence('offline');
    };
  }, [user?.id, updatePresence]);

  return { updatePresence };
}

// Hook to subscribe to incoming chat requests from admins
export function useChatNotifications(user, companyId, onNewChat) {
  useEffect(() => {
    if (!user?.id || !companyId) return;

    // Subscribe to new conversations where this user is the customer
    const channel = supabase
      .channel(`chat-notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_conversations',
          filter: `customer_user_id=eq.${user.id}`
        },
        (payload) => {
          // Admin initiated a chat with this user
          if (payload.new.started_by === 'admin') {
            onNewChat?.(payload.new);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        async (payload) => {
          // Check if this message is for a conversation involving this user
          const { data: conv } = await supabaseData
            .from('chat_conversations')
            .select('*')
            .eq('id', payload.new.conversation_id)
            .eq('customer_user_id', user.id)
            .single();

          if (conv && payload.new.sender_type === 'admin') {
            onNewChat?.({ ...conv, newMessage: payload.new });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, companyId, onNewChat]);
}
